import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  MoreHorizontal,
  AlertTriangle,
} from "lucide-react";
import { ColumnDef, type Column, SortingState } from "@tanstack/react-table";

import { useGetOnboardingSessions } from "@/api/onboarding-session";
import { useResyncOnboardingSession } from "@/api/onboarding-session";
import type { OnboardingSessionSummary } from "@/api/types";
import { formatDate } from "@/lib/date";
import { DataTable } from "@/components/data-table/data-table";
import { DataTableToolbar } from "@/components/data-table/toolbar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  FundingDialogAction,
  SecondaryInviteDialogAction,
} from "@/components/actions/session-actions";

interface SearchParams {
  search?: string;
  pageIndex?: number;
  pageSize?: number;
  sort?: string; // Format: "column1.asc,column2.desc"
}

// Utility functions to convert between URL params and TanStack Table state
function searchParamsToSorting(params: SearchParams): SortingState {
  if (!params.sort) return [{ id: "updatedAt", desc: true }];

  // Parse "column1.asc,column2.desc" format
  return params.sort.split(",").map((sortStr) => {
    const [id, direction] = sortStr.split(".");
    return { id, desc: direction === "desc" };
  });
}

function sortingToSearchParams(
  sorting: SortingState
): Pick<SearchParams, "sort"> {
  if (!sorting.length) return { sort: undefined };

  // Convert to "column1.asc,column2.desc" format
  const sortStr = sorting
    .map((s) => `${s.id}.${s.desc ? "desc" : "asc"}`)
    .join(",");

  return { sort: sortStr };
}

export const Route = createFileRoute("/_auth/")({
  component: DashboardPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => {
    const pageIndex =
      search.pageIndex != null ? Number(search.pageIndex) : undefined;
    const pageSize =
      search.pageSize != null ? Number(search.pageSize) : undefined;

    return {
      search: (search.search as string) || undefined,
      pageIndex:
        pageIndex !== undefined && !isNaN(pageIndex) ? pageIndex : undefined,
      pageSize:
        pageSize !== undefined && !isNaN(pageSize) ? pageSize : undefined,
      sort: (search.sort as string) || undefined,
    };
  },
});

function typeLabel(onboardingType?: string) {
  return onboardingType === "clientInfo"
    ? "Primary Signatory"
    : "Secondary Signatory";
}

function SortHeader({
  column,
  label,
}: {
  column: Column<OnboardingSessionSummary, unknown>;
  label: string;
}) {
  const sorted = column.getIsSorted();
  return (
    <Button
      variant="ghost"
      className="px-0"
      onClick={(e) =>
        column.toggleSorting(sorted === "asc", (e as React.MouseEvent).shiftKey)
      }
      title={
        "Click to sort" +
        (sorted ? ` (${sorted})` : "") +
        "; Shift for multi-sort"
      }
    >
      {label}
      {sorted === "asc" ? (
        <ArrowUp className="ml-2 h-4 w-4" />
      ) : sorted === "desc" ? (
        <ArrowDown className="ml-2 h-4 w-4" />
      ) : (
        <ArrowUpDown className="ml-2 h-4 w-4" />
      )}
    </Button>
  );
}

function DashboardPage() {
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch();
  const { data, isLoading, isError } = useGetOnboardingSessions();
  const resync = useResyncOnboardingSession();

  // Convert URL params to table state
  const search = searchParams.search ?? "";
  const sorting = React.useMemo(
    () => searchParamsToSorting(searchParams),
    [searchParams]
  );
  const pagination = React.useMemo(
    () => ({
      pageIndex: searchParams.pageIndex ?? 0,
      pageSize: searchParams.pageSize ?? 50,
    }),
    [searchParams.pageIndex, searchParams.pageSize]
  );

  // Update URL when state changes
  const setSearch = React.useCallback(
    (value: string) => {
      navigate({
        search: (prev) => ({
          ...prev,
          search: value || undefined,
          pageIndex: 0, // Reset to first page on search
        }),
        replace: true,
      });
    },
    [navigate]
  );

  const onSortingChange = React.useCallback(
    (newSorting: SortingState) => {
      navigate({
        search: (prev) => ({
          ...prev,
          ...sortingToSearchParams(newSorting),
        }),
        replace: true,
      });
    },
    [navigate]
  );

  const onPaginationChange = React.useCallback(
    (newPagination: { pageIndex: number; pageSize: number }) => {
      // Check what the URL values would be
      const targetPageIndex =
        newPagination.pageIndex > 0 ? newPagination.pageIndex : undefined;
      const targetPageSize =
        newPagination.pageSize !== 50 ? newPagination.pageSize : undefined;

      // Only update if the URL values would actually change
      if (
        targetPageIndex === searchParams.pageIndex &&
        targetPageSize === searchParams.pageSize
      ) {
        return;
      }

      navigate({
        search: (prev) => ({
          ...prev,
          pageIndex: targetPageIndex,
          pageSize: targetPageSize,
        }),
        replace: true,
      });
    },
    [navigate, searchParams.pageIndex, searchParams.pageSize]
  );

  // Build memoized link maps: child -> parent, and parent -> first child
  const { childToParent, parentToFirstChild } = React.useMemo(() => {
    const childToParent = new Map<string, string>();
    const parentToFirstChild = new Map<string, string>();
    const list = (data as OnboardingSessionSummary[]) || [];
    for (const s of list) {
      if (s.parentSessionId) {
        childToParent.set(s.sessionId, s.parentSessionId);
        if (!parentToFirstChild.has(s.parentSessionId)) {
          parentToFirstChild.set(s.parentSessionId, s.sessionId);
        }
      }
    }
    return { childToParent, parentToFirstChild };
  }, [data]);

  const columns = React.useMemo<ColumnDef<OnboardingSessionSummary>[]>(
    () => [
      {
        id: "issues",
        header: ({ column }) => <SortHeader column={column} label="Issues" />,
        accessorFn: (row) => {
          const isPrimary = row.onboardingType === "clientInfo";
          const missingSf =
            isPrimary &&
            row.status === "complete" &&
            !row.salesforceOnboardingId;
          const syncFailed = row.salesforceSyncStatus === "sync_failed";
          // Use distinct numbers for each error type combination for better sorting:
          // 0 = no issues, 1 = missing SF only, 2 = sync failed only, 3 = both
          if (missingSf && syncFailed) return 3;
          if (syncFailed) return 2;
          if (missingSf) return 1;
          return 0;
        },
        cell: ({ row }) => {
          const isPrimary = row.original.onboardingType === "clientInfo";
          const missingSf =
            isPrimary &&
            row.original.status === "complete" &&
            !row.original.salesforceOnboardingId;
          const syncFailed =
            row.original.salesforceSyncStatus === "sync_failed";

          if (!missingSf && !syncFailed) return null;

          return (
            <div className="flex justify-center gap-2">
              <TooltipProvider>
                {missingSf && (
                  <Tooltip delayDuration={150}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center text-amber-600">
                        <AlertTriangle className="h-4 w-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Missing salesforce sync</TooltipContent>
                  </Tooltip>
                )}
                {syncFailed && (
                  <Tooltip delayDuration={150}>
                    <TooltipTrigger asChild>
                      <span className="inline-flex items-center text-red-600">
                        <AlertTriangle className="h-4 w-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>Salesforce Sync Failed</TooltipContent>
                  </Tooltip>
                )}
              </TooltipProvider>
            </div>
          );
        },
      },
      {
        accessorKey: "email",
        header: ({ column }) => <SortHeader column={column} label="Email" />,
        cell: ({ getValue }) => String(getValue() ?? ""),
      },
      {
        id: "type",
        header: "Onboarding Type",
        cell: ({ row }) => (
          <Badge variant="secondary">
            {typeLabel(row.original.onboardingType)}
          </Badge>
        ),
      },
      {
        accessorKey: "status",
        header: ({ column }) => <SortHeader column={column} label="Status" />,
        cell: ({ row, getValue }) => {
          const isSecondary = row.original.onboardingType !== "clientInfo";
          const isReserved = row.original.cognitoId === "RESERVED";
          const status =
            isSecondary && isReserved
              ? "Not Started"
              : String(getValue() ?? "");
          return <Badge className="capitalize">{status}</Badge>;
        },
      },
      {
        accessorKey: "createdAt",
        header: ({ column }) => (
          <SortHeader column={column} label="Created Date" />
        ),
        cell: ({ getValue }) => formatDate(getValue<string>()),
      },
      {
        accessorKey: "updatedAt",
        header: ({ column }) => (
          <SortHeader column={column} label="Updated Date" />
        ),
        cell: ({ getValue }) => formatDate(getValue<string>()),
      },
      {
        id: "actions",
        enableHiding: false,
        header: "Actions",
        cell: ({ row }) => {
          const session = row.original;
          const canResendSecondaryInvite =
            session.onboardingType !== "clientInfo" &&
            session.cognitoId === "RESERVED";
          // derive linkedId from maps: if secondary use parent, else first child
          const linkedId =
            childToParent.get(session.sessionId) ??
            parentToFirstChild.get(session.sessionId);
          const linkedSession = linkedId
            ? ((data as OnboardingSessionSummary[]) || []).find(
                (s) => s.sessionId === linkedId
              )
            : undefined;
          const disableFunding = session.status !== "complete";
          return (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem asChild>
                  <Link
                    to="/sessions/$sessionId"
                    params={{ sessionId: session.sessionId }}
                  >
                    View details
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <FundingDialogAction
                  session={session}
                  linkedSession={linkedSession}
                  disabled={disableFunding}
                  trigger={
                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                      Resend funding instructions
                    </DropdownMenuItem>
                  }
                />
                {session.onboardingType !== "clientInfo" && (
                  <SecondaryInviteDialogAction
                    session={session}
                    disabled={!canResendSecondaryInvite}
                    trigger={
                      <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                        Resend secondary signup email
                      </DropdownMenuItem>
                    }
                  />
                )}
                <DropdownMenuSeparator />
                {(() => {
                  const canResync = session.status === "complete";
                  return (
                    <TooltipProvider>
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <span>
                            <DropdownMenuItem
                              disabled={!canResync}
                              onClick={() => {
                                if (!canResync) return;
                                resync.mutate({
                                  cognitoId: session.cognitoId,
                                  sessionId: session.sessionId,
                                });
                              }}
                            >
                              Resync to Salesforce
                            </DropdownMenuItem>
                          </span>
                        </TooltipTrigger>
                        {!canResync ? (
                          <TooltipContent>
                            Resync allowed only when status is complete
                          </TooltipContent>
                        ) : null}
                      </Tooltip>
                    </TooltipProvider>
                  );
                })()}
                <DropdownMenuItem
                  onClick={() =>
                    navigator.clipboard.writeText(session.sessionId)
                  }
                >
                  Copy Session ID
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() =>
                    navigator.clipboard.writeText(session.cognitoId)
                  }
                >
                  Copy Cognito ID
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          );
        },
      },
    ],
    [data, childToParent, parentToFirstChild, resync]
  );

  const sessions: OnboardingSessionSummary[] = Array.isArray(data)
    ? (data as OnboardingSessionSummary[])
    : [];

  if (isLoading) {
    return (
      <section className="grid min-w-0 gap-4 py-2">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-72 w-full" />
      </section>
    );
  }

  if (isError) {
    return (
      <section className="grid min-w-0 gap-2 py-2">
        <p className="text-red-600">Failed to load sessions.</p>
      </section>
    );
  }

  return (
    <section className="grid min-w-0 gap-4 py-2">
      <DataTableToolbar
        value={search}
        onChange={setSearch}
        placeholder="Search email, type or status"
      />
      <DataTable
        columns={columns}
        data={sessions}
        globalFilter={search}
        onGlobalFilterChange={setSearch}
        sorting={sorting}
        onSortingChange={onSortingChange}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        defaultPageSize={50}
        pageSizeOptions={[10, 25, 50, 100, 250, 500]}
      />
    </section>
  );
}

// consolidated action components moved to '@/components/actions/session-actions'
